import React from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import { useSelector } from "react-redux";
import Login from "./pages/Login";
import Registration from "./pages/Registration";
import Dashboard from "./pages/Dashboard";
import Test from "./pages/Test";
import Sidebar from "./components/Sidebar";
import Verification from "./pages/Verification";
import Fees from "./pages/Fees";
import WelfareFees from "./pages/WelfareFees";
import WelfareSlip from "./pages/WelfareSlip";
import FeesSlip from "./pages/FeesSlip";
import Accounts from "./pages/Accounts";
import RegistrationStatus from "./pages/RegistrationStatus";
import Voting from "./pages/Voting";
import PageNotFound from "./pages/PageNotFound";
import DocumentVerification from "./pages/DocumentVerification";
import UserCreationForm from "./pages/UserCreationFrom";
import DeleteAdvocateDetails from "./components/apiComponents/DeleteAdvocateDetails";
import GenerateDefaulterList from "./components/apiComponents/GenerateDefaulterList ";
import TotalRevenue from "./components/TotalRevenue";
import NewRegReq from "./components/apiComponents/NewRegReq";
import TotalRegMembers from "./components/TotalRegMembers";
import Active from "./components/apiComponents/Active";
import Inactive from "./components/apiComponents/Inactive";
import NewAdvocateEntry from "./pages/NewAdvocateEntry";
import LatestVerifiactionDetailsPage from "./components/apiComponents/LatestVerifiactionDetailsPage";
import FeesAndVoucherDetails from "./pages/FeesAndVoucherDetails";
import RejectedRequestDetails from "./components/apiComponents/RejectedRequestDetails";
import GenrateVotingList from "./components/apiComponents/GenrateVotingList";
import FeesDetails from "./components/apiComponents/FeesDetails";
import SignaturePopup from "./components/SignaturePopup";

// Computer Operators who ALSO get Fees + Welfare Fees + New Advocate Entry
// (extra access only — nothing is removed from any existing role)
const FEES_OPERATORS = ["pradeep mantriyan"];

export const isFeesOperator = (user) =>
  user?.role === "Computer Operator" &&
  FEES_OPERATORS.includes(String(user?.userName ?? "").trim().toLowerCase());

function ProtectedRoute({
  children,
  allowedRoles,
  superAdminOnly = false,
  allowIf,
}) {
  const userData = useSelector((state) => state.auth?.userData?.authUser);
  const isLoggedIn = useSelector((state) => state.auth?.isLoggedIn);
  const isFingerPrintVerified = useSelector(
    (state) => state.fingerprint?.isFingerprintVerified
  );

  if (!isLoggedIn || !isFingerPrintVerified) {
    return <Navigate to="/" />;
  }

  const hasOverride = typeof allowIf === "function" && allowIf(userData);

  if (superAdminOnly && userData?.role !== "Super Admin" && !hasOverride) {
    return <Navigate to="/PageNotFound" />;
  }

  if (
    allowedRoles &&
    !allowedRoles.includes(userData?.role) &&
    userData?.role !== "Super Admin" &&
    !hasOverride
  ) {
    return <Navigate to="/PageNotFound" />;
  }

  return children;
}

function App() {
  const isLoggedIn = useSelector((state) => state.auth?.isLoggedIn);
  const isFingerPrintVerified = useSelector(
    (state) => state.fingerprint?.isFingerprintVerified
  );
  const location = useLocation();

  const showSidebar =
    isLoggedIn &&
    isFingerPrintVerified &&
    !["/", "/Registration", "/RegistrationStatus"].includes(location.pathname);

  return (
    <>
      {showSidebar && <Sidebar />}
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/Test" element={<Test />} />
        <Route path="/Registration" element={<Registration />} />

        <Route path="/RegistrationStatus" element={<RegistrationStatus />} />

        {/* Role-Based Routes */}
        <Route
          path="/Accounts"
          element={
            <ProtectedRoute allowedRoles={["Cashier"]}>
              <Accounts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/Fees"
          element={
            <ProtectedRoute
              allowedRoles={["Accountant"]}
              allowIf={isFeesOperator}
            >
              <Fees />
            </ProtectedRoute>
          }
        />
        <Route
          path="/WelfareFees"
          element={
            <ProtectedRoute
              allowedRoles={["Accountant"]}
              allowIf={isFeesOperator}
            >
              <WelfareFees />
            </ProtectedRoute>
          }
        />
        <Route
          path="/WelfareSlip/:barId"
          element={
            <ProtectedRoute
              allowedRoles={["Accountant"]}
              allowIf={isFeesOperator}
            >
              <WelfareSlip />
            </ProtectedRoute>
          }
        />
        <Route
          path="/FeesSlip/:barId"
          element={
            <ProtectedRoute
              allowedRoles={["Accountant"]}
              allowIf={isFeesOperator}
            >
              <FeesSlip />
            </ProtectedRoute>
          }
        />
        <Route
          path="/Verification"
          element={
            <ProtectedRoute allowedRoles={["Computer Operator"]}>
              <Verification />
            </ProtectedRoute>
          }
        />
        <Route
          path="/DocumentVerification"
          element={
            <ProtectedRoute allowedRoles={["Computer Operator"]}>
              <DocumentVerification />
            </ProtectedRoute>
          }
        />
        <Route
          path="/Voting"
          element={
            <ProtectedRoute allowedRoles={["Election"]}>
              <Voting />
            </ProtectedRoute>
          }
        />

        {/* Super Admin route with operator override */}
        <Route
          path="/NewAdvocateEntry"
          element={
            <ProtectedRoute superAdminOnly allowIf={isFeesOperator}>
              <NewAdvocateEntry />
            </ProtectedRoute>
          }
        />

        {/* Super Admin Exclusive Routes */}
        {[
          { path: "/Dashboard", component: <Dashboard /> },

          {
            path: "/FeesAndVoucherDetails",
            component: <FeesAndVoucherDetails />,
          },
          {
            path: "/FeesDetails",
            component: <FeesDetails />,
          },
          {
            path: "/SignaturePopup",
            component: <SignaturePopup />,
          },
          { path: "/NewRegReq", component: <NewRegReq /> },
          {
            path: "/LatestVerifiactionDetailsPage",
            component: <LatestVerifiactionDetailsPage />,
          },
          { path: "/TotalRegMembers", component: <TotalRegMembers /> },
          { path: "/Inactive", component: <Inactive /> },
          { path: "/Active", component: <Active /> },
          { path: "/TotalRevenue", component: <TotalRevenue /> },
          { path: "/UserCreation", component: <UserCreationForm /> },
          {
            path: "/RejectedRequestDetails",
            component: <RejectedRequestDetails />,
          },
          {
            path: "/DeleteAdvocateDetails",
            component: <DeleteAdvocateDetails />,
          },
          { path: "/GenrateVotingList", component: <GenrateVotingList /> },
          {
            path: "/GenerateDefaulterList",
            component: <GenerateDefaulterList />,
          },
        ].map(({ path, component }) => (
          <Route
            key={path}
            path={path}
            element={
              <ProtectedRoute superAdminOnly>{component}</ProtectedRoute>
            }
          />
        ))}

        {/* Catch-All Route */}
        <Route path="/PageNotFound" element={<PageNotFound />} />
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </>
  );
}

export default App;